import {
  applyTableVerb,
  encodeTablePayload,
  type CellAlign,
  type CellBorders,
  type CellRegion,
  type DoczModule,
  type HeaderStyleDto,
  type StructuredBlock,
  type TableBlock,
  type TableStylePreset,
  type TableVerb,
  tableCellAddress,
  type VAlign,
  verbEmptiesTable,
} from "@norkron/web-docz";
import {
  canvasCellAt,
  canvasCellRegion,
  canvasDeleteStructuredBlock,
  canvasDoczBlocks,
  canvasReplaceStructuredBlock,
  canvasSelectTable,
  canvasTableAt,
  canvasTableCellOrds,
  type CanvasEditorHandle,
  handleFromBridge,
} from "@norkron/web-page-layout";
import type {
  CellAlignDto,
  CellBordersDto,
  CellVAlignDto,
  TableCellRange,
  TableEditVerbs,
} from "@norkron/web-elaborate-editor";

/**
 * `canvasTableLayer` — the CANVAS half of the contextual Table ribbon group.
 *
 * ## What was broken (owner P0, 2026-07-29)
 *
 * `<CanvasElaborateEditor>` published `inTable: () => false` and a `NOOP_TABLE`
 * whose sixteen verbs all returned `false`. `inTable` is the ONE gate the ribbon
 * reads to decide whether the contextual Table TAB exists at all
 * (`web-elaborate-editor` `commands/resolve.ts`: `CONTEXTUAL_TABS = { table:
 * 'inTable' }`), so on the canvas — the default editing surface — the Table group
 * could never appear, however many tables the document had and wherever the caret
 * sat. Every one of the 21 table commands was unreachable.
 *
 * ## Why it is buildable now, and was not before
 *
 * The stub's comment blamed "no canvas cell model (TODO C4) — blocked on the pdfz
 * per-cell geometry provenance FR". That FR LANDED: pdfz emits `cellRow`/`cellCol`
 * (SoA v5) plus `tableOrd` (v7), `web-docz cbb17d7` projects ONE `table_cell`
 * block per cell carrying its sub-block address (`T::th:2`, `T::td:3:1`), and
 * `web-page-layout e9f9de8` routes an edit to one. So `inTable` is no longer a
 * question about a palette store of tables the user inserted THIS SESSION (which
 * is what the DOM controller's `hasTableTarget()` answers, and why a loaded
 * document's tables were invisible to it) — it is a property of the caret's own
 * geometry, true for every table in the document including the ones that arrived
 * from the server.
 *
 * ## The one implementation, not a second one
 *
 * RULE ZERO all the way down. The grid math (insert/delete/merge/split, the cell
 * channels) executes in `docz.wasm` behind `@norkron/web-docz`'s `applyTableVerb`;
 * the caret → `(table, cell, region)` derivation is `@norkron/web-page-layout`'s
 * `canvasCellAt` / `canvasCellRegion`; the style presets are web-docz's
 * `TABLE_STYLE_PRESETS`. This file resolves the caret, calls one verb, and
 * installs the produced document as ONE undoable step via
 * `EditorCore.applyBridge`. It frames no table bytes and owns no table model.
 *
 * ## The refusal contract
 *
 * Every verb returns `boolean`, and `false` means NOTHING MOVED — never a
 * fabricated success. A verb declines when: no caret is in a cell, the bridge is
 * not docz-backed (read mode), `applyTableVerb` reports a precondition failure, or
 * the produced bridge is identical to the current one. The ribbon renders these
 * commands DISABLED (`contextScope: 'table'` → `env.inTable`), so a decline is
 * normally unreachable from the UI — it is the honest floor beneath it.
 */

export interface CanvasTableLayerDeps {
  /** The live canvas editor handle (`null` before mount / after dispose). */
  getEditor(): CanvasEditorHandle | null;
}

export interface CanvasTableLayer {
  /** The 16 `TableEditVerbs` the ribbon's Table group drives. */
  readonly verbs: TableEditVerbs;
  /** True when the caret sits inside a table cell — `ToolbarEnv.inTable`, the
   *  gate the contextual Table TAB's very existence hangs on. */
  inTable(): boolean;
  /** The active rectangular cell region, or `null` outside a table. */
  tableSelection(): TableCellRange | null;
  /** True when {@link tableSelection} spans MORE THAN ONE cell — the gate
   *  `table.merge` reads (`ToolbarEnv.hasCellRange`). */
  hasCellRange(): boolean;
  /** True when the caret's table has a structural header row — so
   *  `table.header-toggle` can render PRESSED and actually toggle OFF. */
  tableHeaderOn(): boolean;
}

/** The caret's live table context, resolved once per verb. */
interface Ctx {
  readonly editor: CanvasEditorHandle;
  readonly module: DoczModule;
  readonly tableId: string;
  readonly table: TableBlock;
  readonly region: CellRegion;
  /** The docz address of the FOCUS cell — where the caret is re-projected to. */
  readonly focusAddr: { readonly header: boolean; readonly row: number; readonly col: number };
  /** The document ordinal of the table's first projected cell. */
  readonly firstCellOrd: number;
}

export function createCanvasTableLayer(deps: CanvasTableLayerDeps): CanvasTableLayer {
  /** Resolve everything a verb needs, or `null` for an honest decline. */
  function ctx(): Ctx | null {
    const editor = deps.getEditor();
    if (!editor) return null;
    const { bridge, selection } = editor.core.getState();
    const region = canvasCellRegion(bridge, selection);
    if (!region) return null;
    const table = canvasTableAt(bridge, selection.focus.blockOrd);
    if (!table) return null;
    const module = handleFromBridge(bridge)?.module;
    if (!module) return null;
    const f = region.focus;
    return {
      editor,
      module,
      tableId: table.id,
      table: table.table,
      region: { r0: region.r0, c0: region.c0, r1: region.r1, c1: region.c1 },
      focusAddr: { header: f.header, row: f.row, col: f.col },
      firstCellOrd: f.firstCellOrd,
    };
  }

  /**
   * Apply one verb: mutate the table in `docz.wasm`, install the produced document
   * as ONE undoable step, and re-project the caret.
   *
   * THE CARET IS RE-PROJECTED BY ADDRESS, NOT BY ORDINAL. A structural verb moves
   * every cell ordinal after it — insert a column and each subsequent row's cells
   * shift — so keeping the old `blockOrd` would land the caret in a DIFFERENT cell
   * (the same class of defect as the click that used to go nowhere, arriving from
   * the other direction). The address `T::td:r:c` survives the mutation when the
   * cell survives it; when the cell is gone (its row or column was deleted) the
   * caret clamps to the table's first cell rather than to a stale index.
   */
  function apply(verb: TableVerb): boolean {
    const c = ctx();
    if (!c) return false;
    const next = applyTableVerb(c.module, c.table, c.region, verb);
    if (!next) return false;
    // A VERB THAT CHANGED NOTHING DID NOT APPLY. `applyTableVerb` returns `null`
    // for a precondition failure, but some `dz_table_*` verbs answer OK while
    // leaving the grid alone — `splitCell` on a cell that was never merged is the
    // measured case, and its `intoRows`/`intoCols` arguments are inert upstream.
    // Without this the verb would install an identical document, report `true`,
    // and burn an undo step: a fabricated success, which is the failure class this
    // whole change exists to remove. Compared on docz's OWN canonical bytes rather
    // than a hand-rolled deep-equal, so a channel added upstream is covered
    // automatically.
    if (sameTable(c.table, next)) return false;
    const bridge = c.editor.core.getState().bridge;
    const nextBridge = canvasReplaceStructuredBlock(bridge, c.tableId, {
      ...next,
      id: c.tableId,
    } as StructuredBlock);
    // Identity is the bridge's own honest "nothing moved" signal (the `wrap`
    // convention upstream) — reporting `true` for it would be a fabricated success.
    if (!nextBridge || nextBridge === bridge) return false;
    const ord = cellOrdFor(nextBridge, c.tableId, c.focusAddr) ?? c.firstCellOrd;
    const at = { blockOrd: ord, byteOffset: 0, affinity: "downstream" as const };
    c.editor.core.applyBridge(nextBridge, { selection: { anchor: at, focus: at } });
    return true;
  }

  /** Whether `verb` at the live caret region would leave the table with no cells
   *  at all — the question `deleteRow`/`deleteColumn` must ask before applying, so
   *  an emptying delete becomes a table deletion instead of a husk. */
  function wouldEmpty(verb: TableVerb): boolean {
    const c = ctx();
    return c != null && verbEmptiesTable(c.table, c.region, verb);
  }

  /** The document ordinal of the cell a docz address names in `bridge`, or `null`
   *  when the mutation removed it. Resolved by ID — the address IS the identity. */
  function cellOrdFor(
    bridge: Parameters<typeof canvasDoczBlocks>[0],
    tableId: string,
    addr: { header: boolean; row: number; col: number },
  ): number | null {
    const want = tableCellAddress(tableId, addr);
    const blocks = canvasDoczBlocks(bridge);
    if (!blocks) return null;
    const i = blocks.findIndex((b) => b.id === want);
    return i < 0 ? null : i;
  }

  /**
   * Select the caret's table AS ONE OBJECT.
   *
   * The range is an ordinary `Selection` over the table's contiguous cell
   * ordinals (`canvasSelectTable`) installed through the SAME `core.setSelection`
   * a drag uses — there is no second notion of "a table is selected" anywhere, so
   * Copy, the painter, the ribbon and undo all keep reading the one selection they
   * already read.
   */
  function selectTable(): boolean {
    const editor = deps.getEditor();
    if (!editor) return false;
    const { bridge, selection } = editor.core.getState();
    const sel = canvasSelectTable(bridge, selection.focus.blockOrd);
    if (!sel) return false;
    editor.core.setSelection(sel);
    return true;
  }

  /**
   * Delete the caret's table — the whole block.
   *
   * ⚠️ THE SAVE REFUSAL IS NOT TRIPPED BY THIS, and that is by construction rather
   * than by exemption. `structuralLossOnSave` compares the LIVE model
   * (`handle.toBlocks()`) against the bytes about to be written; a deliberate
   * deletion has already removed the table from the model, so BOTH sides have
   * dropped it and the loss list is empty. The refusal only ever fires when the
   * serializer destroys structure the model still holds — which is exactly the
   * defect it was built for, and it keeps firing for that.
   *
   * The caret lands on the block that took the table's place (or the one before it
   * when the table ended the document), never on a stale ordinal.
   */
  function deleteTable(): boolean {
    const editor = deps.getEditor();
    if (!editor) return false;
    const { bridge, selection } = editor.core.getState();
    const table = canvasTableAt(bridge, selection.focus.blockOrd);
    if (!table) return false;
    const span = canvasTableCellOrds(bridge, selection.focus.blockOrd);
    const nextBridge = canvasDeleteStructuredBlock(bridge, table.id);
    if (!nextBridge || nextBridge === bridge) return false;
    // The table's cells occupied `[first, last]`; after the removal the block that
    // followed them sits at `first`. Clamp to the last block when the table ended
    // the document, and to 0 when nothing is left but the editable floor.
    const wanted = span ? span.first : 0;
    const ord = Math.max(0, Math.min(wanted, nextBridge.blocks.length - 1));
    const at = { blockOrd: ord, byteOffset: 0, affinity: "downstream" as const };
    editor.core.applyBridge(nextBridge, { selection: { anchor: at, focus: at } });
    return true;
  }

  const verbs: TableEditVerbs = {
    selectTable,
    deleteTable,
    insertRow: (where) => apply({ op: "insertRow", where }),
    // WORD PARITY, AND THE HUSK GUARD. Deleting the last remaining row used to
    // leave a zero-row table: still a block, still counted by the structural
    // census, projecting NO cells — so no caret could enter it, the contextual
    // Table tab never appeared again, and no verb could ever be aimed at it. The
    // grid could be emptied and then never removed. `applyTableVerb` now declines
    // that verb outright; asking web-docz FIRST whether it would empty the table
    // turns the decline into what the user meant, which is what Word and Docs do.
    deleteRow: () => (wouldEmpty({ op: "deleteRow" }) ? deleteTable() : apply({ op: "deleteRow" })),
    insertColumn: (where) => apply({ op: "insertColumn", where }),
    deleteColumn: () =>
      wouldEmpty({ op: "deleteColumn" }) ? deleteTable() : apply({ op: "deleteColumn" }),
    mergeCells: () => apply({ op: "mergeCells" }),
    splitCell: (intoRows, intoCols) => apply({ op: "splitCell", intoRows, intoCols }),
    setCellShading: (hex) => apply({ op: "setCellShading", hex }),
    setCellVAlign: (v) => apply({ op: "setCellVAlign", v: v as VAlign | null }),
    setCellBorders: (b) => apply({ op: "setCellBorders", borders: b as CellBorders | null }),
    setCellAlignH: (a) => apply({ op: "setCellAlignH", a: a as CellAlign | null }),
    toggleHeader: (on) => apply({ op: "toggleHeader", on }),
    setColumnWidth: (col, pt) => apply({ op: "setColumnWidth", col, pt }),
    setRowHeadColumns: (n) => apply({ op: "setRowHeadColumns", n }),
    applyTableStyle: (preset) => apply({ op: "applyTableStyle", preset: preset as TableStylePreset }),
    setHeaderStyle: (style) => apply({ op: "setHeaderStyle", style: style as HeaderStyleDto | null }),
    setRowBanding: (on, color) => apply({ op: "setRowBanding", on, color }),
  };

  return {
    verbs,
    inTable(): boolean {
      const editor = deps.getEditor();
      if (!editor) return false;
      const { bridge, selection } = editor.core.getState();
      return canvasCellAt(bridge, selection.focus.blockOrd) !== null;
    },
    tableSelection(): TableCellRange | null {
      const editor = deps.getEditor();
      if (!editor) return null;
      const { bridge, selection } = editor.core.getState();
      const r = canvasCellRegion(bridge, selection);
      if (!r) return null;
      return { blockId: r.focus.tableId, r0: r.r0, c0: r.c0, r1: r.r1, c1: r.c1 };
    },
    hasCellRange(): boolean {
      const editor = deps.getEditor();
      if (!editor) return false;
      const { bridge, selection } = editor.core.getState();
      const r = canvasCellRegion(bridge, selection);
      if (!r) return false;
      return r.r1 > r.r0 || r.c1 > r.c0;
    },
    tableHeaderOn(): boolean {
      const editor = deps.getEditor();
      if (!editor) return false;
      const { bridge, selection } = editor.core.getState();
      const t = canvasTableAt(bridge, selection.focus.blockOrd);
      return t != null && t.table.header != null;
    },
  };
}

// Re-exported so the suites can name the DTO shapes the ribbon passes through
// without importing three packages.
export type { CellAlignDto, CellBordersDto, CellVAlignDto };

/** Do two tables carry the SAME document content? Compared on docz's own
 *  canonical wire bytes (`encodeTablePayload` — the leading DTO every `dz_table_*`
 *  verb consumes), so every cell channel, span, column spec and run mark is in
 *  scope and a channel added upstream is covered without touching this file. */
function sameTable(a: TableBlock, b: TableBlock): boolean {
  const x = encodeTablePayload(a);
  const y = encodeTablePayload(b);
  if (x.length !== y.length) return false;
  for (let i = 0; i < x.length; i += 1) if (x[i] !== y[i]) return false;
  return true;
}
